import { lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import { brand, contactPage, offices } from "@/content/site";
import { OfficePartner } from "@/components/common/OfficePartner";
import { contactForm } from "@/content/forms";
import usePageMeta from "@/hooks/usePageMeta";
import { PageHero } from "@/components/sections/PageHero";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { WhatsAppIcon } from "@/components/common/WhatsAppIcon";
import { Rich } from "@/components/common/Rich";
import { Reveal } from "@/components/motion/Reveal";
import { whatsappLink } from "@/lib/whatsapp";

// Leaflet is loaded only on this page to keep the rest of the site light.
const LiveMap = lazy(() => import("@/components/sections/LiveMap"));

const OfficeMap = () => (
  <div className="relative isolate z-0 overflow-hidden border border-line bg-surface" data-testid="contact-office-map">
    <Suspense fallback={<div className="h-[360px] w-full sm:h-[460px]" />}>
      <LiveMap />
    </Suspense>
  </div>
);

const Aside = () => (
  <div className="space-y-4">
    <a href={`mailto:${brand.email}`} className="flex items-center gap-4 border border-line bg-surface p-5 transition-colors hover:border-accent" data-testid="contact-email-link">
      <Mail className="h-5 w-5 text-accent" />
      <span>
        <span className="block text-[13px] text-muted">Email</span>
        <span className="break-all text-[15px] text-ink">{brand.email}</span>
      </span>
    </a>
    <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 border border-line bg-surface p-5 transition-colors hover:border-accent" data-testid="contact-whatsapp-link">
      <WhatsAppIcon className="h-5 w-5 text-accent" />
      <span>
        <span className="block text-[13px] text-muted">WhatsApp</span>
        <span className="text-[15px] text-ink"><Rich text={brand.whatsappLabel} /></span>
      </span>
    </a>
    {offices.map((o) => (
      <div key={o.id} className="flex items-start gap-4 border border-line bg-surface p-5" data-testid={`contact-office-${o.id}`}>
        <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
        <span>
          <span className="block text-[15px] font-medium text-ink">{o.country}, {o.role}</span>
          <span className="mt-1 block text-[14px] text-muted">{o.address || o.city}</span>
          <OfficePartner office={o} className="mt-2 text-[14px] text-muted" />
        </span>
      </div>
    ))}
  </div>
);

export default function Contact() {
  usePageMeta(contactPage.meta);
  const [params] = useSearchParams();
  const topic = params.get("topic");
  const initial = contactForm.fields.find((f) => f.name === "topic").options.includes(topic) ? { topic } : {};

  return (
    <>
      <PageHero hero={contactPage.hero} testId="contact-hero" />
      <section className="pb-24 sm:pb-32" data-testid="contact-section">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <div className="relative border border-line bg-surface p-6 sm:p-10">
              <EnquiryForm key={topic || "general"} config={contactForm} idPrefix="contact" initialValues={initial} />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4">
            <Aside />
          </Reveal>
        </div>
        <div className="container-x mt-16">
          <Reveal>
            <OfficeMap />
          </Reveal>
        </div>
      </section>
    </>
  );
}
